import { Box, Modal } from '@mui/material'
import { get, has } from 'lodash'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useRef, useState } from 'react'

// USHOP-2821 — self-contained 3DS challenge (step-up) renderer for the Send Now subscription flow.
// Mirrors the proven checkout round-trip (WorldPay/Cardinal) WITHOUT touching the live checkout
// component: the challengeHTML is injected into the /wp-iframe.html proxy iframe; the gateway runs the
// step-up, redirects to /payment-response/done (inside the iframe), which posts WP_3DS_SUCCESS/FAILED to
// this window. The message protocol is endpoint-agnostic, so the same flow works for Send Now.

const decodeWorldPayHTML = html => {
    if (!html) return ''
    try {
        return decodeURIComponent(html)
    } catch (error) {
        return html
    }
}

const getParameterByName = (name, url) => {
    const targetUrl = url || window.location.href
    try {
        const parsedUrl = new URL(targetUrl, window.location.origin)
        return parsedUrl.searchParams.get(name)
    } catch (error) {
        console.error('Invalid redirect URL while parsing parameter', error)
        return null
    }
}

const injectHtmlIntoIframe = (iframeRef, html) => {
    if (!iframeRef?.current || !html) {
        return
    }
    const iframeDocument = iframeRef.current.contentWindow?.document
    if (!iframeDocument) {
        return
    }
    try {
        iframeDocument.open()
        iframeDocument.write(html)
        iframeDocument.close()
    } catch (error) {
        console.error('Failed to inject challenge HTML into iframe', error)
    }
}

const SendNowThreeDS = observer(({ challengeHTML, fallbackOrderId, onSuccess, onFailure, onClose }) => {
    const challengeIframeRef = useRef(null)
    const settledRef = useRef(false)
    const [iframeReady, setIframeReady] = useState(false)

    const open = Boolean(challengeHTML)

    const settle = useCallback(
        (handler, arg) => {
            if (settledRef.current) return
            settledRef.current = true
            handler?.(arg)
        },
        []
    )

    // Inject the challenge HTML once the proxy iframe has loaded.
    useEffect(() => {
        if (!open || !iframeReady || !challengeHTML) {
            return
        }
        injectHtmlIntoIframe(challengeIframeRef, decodeWorldPayHTML(challengeHTML))
    }, [open, iframeReady, challengeHTML])

    // Listen for the post-authentication result posted back by /payment-response/done.
    useEffect(() => {
        const handleMessage = event => {
            const { data } = event || {}
            if (!data || data === 'PROXY_READY') {
                return
            }

            let msg
            try {
                msg = typeof data === 'string' ? JSON.parse(data) : data
            } catch (error) {
                settle(onFailure, 'something_went_wrong_with_payment')
                return
            }

            if (msg?.type === 'WP_3DS_SUCCESS') {
                settle(onSuccess, msg.order_id || fallbackOrderId || '')
                return
            }

            if (msg?.type === 'WP_3DS_FAILED') {
                settle(onFailure, msg.error_message || 'something_went_wrong_with_payment')
                return
            }

            // Fallback: a direct redirectURL message carrying a tppPayload.
            const redirectURL = msg?.redirectURL
            if (!redirectURL) {
                return
            }
            const tppPayload = getParameterByName('tppPayload', redirectURL)
            if (!tppPayload) {
                return
            }
            let tpp
            try {
                tpp = JSON.parse(tppPayload)
            } catch (error) {
                settle(onFailure, 'something_went_wrong_with_payment')
                return
            }
            if (!has(tpp, 'eventDetails')) {
                return
            }
            const orderId = get(tpp, 'eventDetails.id.unicity')
            if (orderId) {
                settle(onSuccess, orderId)
                return
            }
            const errorMessage = get(tpp, 'eventDetails.error.error_message') || get(tpp, 'eventDetails.error')
            settle(onFailure, errorMessage || 'something_went_wrong_with_payment')
        }

        window.addEventListener('message', handleMessage)
        return () => {
            window.removeEventListener('message', handleMessage)
        }
    }, [settle, onSuccess, onFailure, fallbackOrderId])

    if (!open) {
        return null
    }

    return (
        <Modal open={open} aria-labelledby="send-now-3ds-challenge" aria-describedby="send-now-3ds-challenge-description">
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 420,
                    bgcolor: 'background.paper',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.18)'
                }}>
                <Box
                    component="button"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 12,
                        background: 'transparent',
                        border: 'none',
                        fontSize: 20,
                        cursor: 'pointer',
                        color: '#555',
                        padding: '4px 8px',
                        zIndex: 1,
                        lineHeight: 1
                    }}
                    aria-label="Close">
                    ✕
                </Box>
                <iframe
                    id="wpStepUp"
                    ref={challengeIframeRef}
                    sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-top-navigation allow-top-navigation-by-user-activation allow-modals allow-popups-to-escape-sandbox allow-presentation"
                    src="/wp-iframe.html"
                    style={{
                        width: '100%',
                        height: '600px',
                        border: 'none',
                        display: 'block',
                        scrollbarWidth: 'none'
                    }}
                    title="3DS Challenge"
                    onLoad={() => setIframeReady(true)}
                />
            </Box>
        </Modal>
    )
})

export default SendNowThreeDS
